import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_CLIENT_BULLETIN_POSTS } from '../graphql/clientQueries';
import { ADD_REACTION, REMOVE_REACTION } from '../../graphql/bulletinBoard';
import { useClientAuth } from '../hooks/useClientAuth';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { VideoConferencesSection } from '../components/VideoConferencesSection';
import type { BulletinPost, BulletinPostsResponse } from '../types';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import toast from 'react-hot-toast';

const POSTS_PER_PAGE = 10;

/**
 * NewsScreen - Client bulletin board / news feed
 * 
 * Displays bulletin posts visible to clients with:
 * - Pinned posts first, then sorted by date
 * - Infinite scroll pagination
 * - Post detail modal (read-only)
 * - Pull-to-refresh support
 * 
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6
 */
export function NewsScreen() {
  const { user } = useClientAuth();
  const [selectedPost, setSelectedPost] = useState<BulletinPost | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const { data, loading, error, fetchMore, refetch } = useQuery<{
    listMyBulletinPosts: BulletinPostsResponse;
  }>(GET_CLIENT_BULLETIN_POSTS, {
    variables: { limit: POSTS_PER_PAGE },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [addReaction] = useMutation(ADD_REACTION);
  const [removeReaction] = useMutation(REMOVE_REACTION);

  const posts = data?.listMyBulletinPosts?.items || [];
  const nextToken = data?.listMyBulletinPosts?.nextToken;

  // Sort posts: pinned first, then by createdAt date (newest first)
  const sortedPosts = [...posts].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

  // Infinite scroll observer
  const handleLoadMore = useCallback(() => {
    if (nextToken && !loading) {
      fetchMore({
        variables: { limit: POSTS_PER_PAGE, nextToken },
        updateQuery: (
          prev: { listMyBulletinPosts: BulletinPostsResponse },
          { fetchMoreResult }: { fetchMoreResult?: { listMyBulletinPosts: BulletinPostsResponse } }
        ) => {
          if (!fetchMoreResult) return prev;
          return {
            listMyBulletinPosts: {
              ...fetchMoreResult.listMyBulletinPosts,
              items: [
                ...prev.listMyBulletinPosts.items,
                ...fetchMoreResult.listMyBulletinPosts.items,
              ],
            },
          };
        },
      });
    }
  }, [nextToken, loading, fetchMore]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleLoadMore]);

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFullTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateContent = (content: string, maxLength = 200): string => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const getPriorityConfig = (priority?: string) => {
    switch (priority) {
      case 'URGENT':
        return { color: 'bg-red-500', icon: '🚨', label: 'URGENT' };
      case 'IMPORTANT':
        return { color: 'bg-amber-500', icon: '⚠️', label: 'IMPORTANT' };
      default:
        return null;
    }
  };

  const getTotalReactions = (reactions?: BulletinPost['reactions']) => {
    if (!reactions) return 0;
    return (
      (reactions.thumbsUp?.length || 0) +
      (reactions.heart?.length || 0) +
      (reactions.party?.length || 0) +
      (reactions.eyes?.length || 0)
    );
  };

  const getReactionCount = (reactions: BulletinPost['reactions'], emoji: string): number => {
    const emojiMap: { [key: string]: keyof NonNullable<BulletinPost['reactions']> } = {
      '👍': 'thumbsUp',
      '❤️': 'heart',
      '🎉': 'party',
      '👀': 'eyes',
    };
    const key = emojiMap[emoji];
    return reactions?.[key]?.length || 0;
  };

  const hasUserReacted = (reactions: BulletinPost['reactions'], emoji: string): boolean => {
    if (!user?.id || !reactions) return false;
    const emojiMap: { [key: string]: keyof NonNullable<BulletinPost['reactions']> } = {
      '👍': 'thumbsUp',
      '❤️': 'heart',
      '🎉': 'party',
      '👀': 'eyes',
    };
    const key = emojiMap[emoji];
    return reactions[key]?.includes(user.id) || false;
  };

  const handleReaction = async (postId: string, emoji: string, currentlyReacted: boolean) => {
    try {
      const emojiKey = emoji === '👍' ? 'thumbsUp' : emoji === '❤️' ? 'heart' : emoji === '🎉' ? 'party' : 'eyes';

      if (currentlyReacted) {
        await removeReaction({ variables: { postId, emoji: emojiKey } });
      } else {
        await addReaction({ variables: { postId, emoji: emojiKey } });
      }
      refetch();
    } catch (err) {
      console.error('Error updating reaction:', err);
      toast.error('Failed to update reaction');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType: string): string => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('text')) return '📃';
    return '📎';
  };

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Get lightbox slides for selected post
  const getLightboxSlides = (post: BulletinPost) => {
    const slides: { src: string }[] = [];
    if (post.attachments) {
      post.attachments.filter(a => a.isImage).forEach(a => slides.push({ src: a.url }));
    }
    if (post.images) {
      post.images.forEach(img => slides.push({ src: img.url }));
    }
    return slides;
  };

  if (error) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-4">😕</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load news</h2>
        <p className="text-gray-600 mb-4">Please try again later</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-aalaw-red/10 flex items-center justify-center">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Welcome to AA Law</h1>
            <p className="text-sm text-gray-500">
              {sortedPosts.length} announcement{sortedPosts.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 overscroll-contain scrollbar-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
        {loading && posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-aalaw-red mb-4"></div>
            <p className="text-gray-500">Loading news...</p>
          </div>
        ) : sortedPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            {/* Video Conferences Section - show even when no posts */}
            <VideoConferencesSection maxItems={3} />
            <div className="text-6xl mb-4">📢</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No posts yet</h2>
            <p className="text-gray-500">Check back later for updates</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Video Conferences Section */}
            <VideoConferencesSection maxItems={3} />
            
            {sortedPosts.map((post) => {
              const fullPost = post as BulletinPost;
              const priorityConfig = getPriorityConfig(fullPost.priority);
              const totalReactions = getTotalReactions(fullPost.reactions);
              const contentPreview = truncateContent(post.content, 150);

              return (
                <Card
                  key={post.id}
                  className={`cursor-pointer hover:shadow-md transition-shadow border-l-4 ${
                    priorityConfig ? 'border-l-red-500' : 'border-l-aalaw-red'
                  }`}
                  onClick={() => setSelectedPost(fullPost)}
                >
                  <div className="p-4 sm:p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {priorityConfig && (
                          <Badge className={`${priorityConfig.color} text-white`}>
                            <span className="mr-1">{priorityConfig.icon}</span>
                            {priorityConfig.label}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        {post.isPinned && <span title="Pinned">📌</span>}
                        <span>{formatTimestamp(fullPost.createdAt || '')}</span>
                      </div>
                    </div>

                    {/* Content Preview */}
                    <p className="text-gray-700 leading-relaxed mb-3">{contentPreview}</p>

                    {/* Attachments indicator */}
                    {post.attachments && post.attachments.length > 0 && (
                      <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span>{post.attachments.length} attachment{post.attachments.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}

                    {/* Reactions */}
                    {totalReactions > 0 && (
                      <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                        {fullPost.reactions?.thumbsUp && fullPost.reactions.thumbsUp.length > 0 && (
                          <span className="flex items-center gap-1 text-sm">
                            <span>👍</span>
                            <span className="text-gray-500">{fullPost.reactions.thumbsUp.length}</span>
                          </span>
                        )}
                        {fullPost.reactions?.heart && fullPost.reactions.heart.length > 0 && (
                          <span className="flex items-center gap-1 text-sm">
                            <span>❤️</span>
                            <span className="text-gray-500">{fullPost.reactions.heart.length}</span>
                          </span>
                        )}
                        {fullPost.reactions?.party && fullPost.reactions.party.length > 0 && (
                          <span className="flex items-center gap-1 text-sm">
                            <span>🎉</span>
                            <span className="text-gray-500">{fullPost.reactions.party.length}</span>
                          </span>
                        )}
                        {fullPost.reactions?.eyes && fullPost.reactions.eyes.length > 0 && (
                          <span className="flex items-center gap-1 text-sm">
                            <span>👀</span>
                            <span className="text-gray-500">{fullPost.reactions.eyes.length}</span>
                          </span>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      {contentPreview.endsWith('...') && (
                        <span className="text-sm text-aalaw-red font-medium">Read more →</span>
                      )}
                      <span className="text-xs text-gray-400 italic ml-auto">
                        Posted by {fullPost.creator ? `${fullPost.creator.firstName} ${fullPost.creator.lastName}` : 'AA Law'}
                      </span>
                    </div>
                  </div>
                </Card>
              );
            })}

            {/* Load more trigger */}
            {nextToken && (
              <div ref={loadMoreRef} className="flex justify-center py-4">
                {loading && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-aalaw-red"></div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Post Detail Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">Post Details</h3>
                {selectedPost.isPinned && <span title="Pinned">📌</span>}
                {getPriorityConfig(selectedPost.priority) && (
                  <Badge className={`${getPriorityConfig(selectedPost.priority)!.color} text-white`}>
                    <span className="mr-1">{getPriorityConfig(selectedPost.priority)!.icon}</span>
                    {getPriorityConfig(selectedPost.priority)!.label}
                  </Badge>
                )}
              </div>
              <button
                onClick={() => setSelectedPost(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                {/* Metadata */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">
                    {formatFullTimestamp(selectedPost.createdAt || '')}
                  </p>
                  <p className="text-base font-semibold text-gray-900">
                    Posted by {selectedPost.creator ? `${selectedPost.creator.firstName} ${selectedPost.creator.lastName}` : 'AA Law'}
                  </p>
                </div>

                {/* Content with Markdown */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 prose prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const isInline = !match;
                        return isInline ? (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        ) : (
                          // @ts-ignore
                          <SyntaxHighlighter style={vscDarkPlus as any} language={match[1]} PreTag="div">
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        );
                      },
                    }}
                  >
                    {selectedPost.content}
                  </ReactMarkdown>
                </div>

                {/* Attachments */}
                {((selectedPost.attachments && selectedPost.attachments.length > 0) ||
                  (selectedPost.images && selectedPost.images.length > 0)) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Attachments</h4>

                    {/* Image Attachments */}
                    {selectedPost.attachments && selectedPost.attachments.filter(a => a.isImage).length > 0 && (
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {selectedPost.attachments.filter(a => a.isImage).map((attachment, idx) => (
                          <div
                            key={attachment.id}
                            className="relative group cursor-pointer"
                            onClick={() => openLightbox(idx)}
                          >
                            <img
                              src={attachment.url}
                              alt={attachment.fileName}
                              className="w-full h-32 object-cover rounded-lg group-hover:opacity-80 transition-opacity"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate">
                              {attachment.fileName}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Legacy Images */}
                    {selectedPost.images && selectedPost.images.length > 0 && (
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {selectedPost.images.map((image, idx) => (
                          <div
                            key={image.id}
                            className="relative group cursor-pointer"
                            onClick={() => {
                              const offset = selectedPost.attachments?.filter(a => a.isImage).length || 0;
                              openLightbox(offset + idx);
                            }}
                          >
                            <img
                              src={image.thumbnailUrl || image.url}
                              alt={`Image ${idx + 1}`}
                              className="w-full h-32 object-cover rounded-lg group-hover:opacity-80 transition-opacity"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Document Attachments */}
                    {selectedPost.attachments && selectedPost.attachments.filter(a => !a.isImage).length > 0 && (
                      <div className="space-y-2">
                        {selectedPost.attachments.filter(a => !a.isImage).map((attachment) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className="text-2xl flex-shrink-0">{getFileIcon(attachment.fileType)}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{attachment.fileName}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(attachment.fileSize)}</p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(attachment.url, attachment.fileName);
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm text-aalaw-red hover:bg-aalaw-red hover:text-white rounded-md transition-colors flex-shrink-0"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Reactions */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Reactions</h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    {['👍', '❤️', '🎉', '👀'].map((emoji) => {
                      const count = getReactionCount(selectedPost.reactions, emoji);
                      const hasReacted = hasUserReacted(selectedPost.reactions, emoji);

                      return (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(selectedPost.id, emoji, hasReacted)}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm transition-all ${
                            hasReacted
                              ? 'bg-aalaw-red/10 border-2 border-aalaw-red text-aalaw-red'
                              : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200'
                          }`}
                        >
                          <span className="text-lg">{emoji}</span>
                          {count > 0 && <span className="text-xs font-medium">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200">
              <Button variant="outline" onClick={() => setSelectedPost(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && selectedPost && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={getLightboxSlides(selectedPost)}
          index={lightboxIndex}
        />
      )}
    </div>
  );
}
